import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { CalendarItem, CalendarItemType, CalendarTitle, CalendarViews, GroupedCalendarItems } from 'kfbs-nglib';
import { forkJoin, Observable, of, Subject } from 'rxjs';
import { catchError, map, shareReplay, takeUntil, tap } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { LogCategory, LoggerService, LogService, LogType } from '../core/services/logger.service';

@Injectable()
export class CalendarService {

    private _reload$ = new Subject<void>();
    private _calendar$: Observable<any> = null;
    private _agendaCalendar$: Observable<any> = null;
    public isEmployeeCal: boolean = false;

    constructor(
        private http: HttpClient,
        private logger: LoggerService,
    ) {
        this.log("constructor", null, LogCategory.Constructor);
    }

    private log(method: string = null, message = null, category: LogCategory = LogCategory.Default): void {
        method = (method) ? '.' + method + '()' : '';
        message = (message) ? message.toString() : '';
        this.logger.try(this.constructor.name + method + ': ' + message, LogType.Service, LogService.Calendar, category);
    }

    private get schoolCalendar$(): Observable<any> {
        const url = `${environment.apiUrl}/calendar/school`;
        return this.http.get<any>(url)
            .pipe(
                map((res) => {
                    this.isEmployeeCal = res.isEmployee;
                    return res.schoolCalendar;
                }),
                catchError(err => of([])));
    }

    private get canvasCalendar$(): Observable<any> {
        const url = `${environment.apiUrl}/calendar/canvas`;
        return this.http.get<any>(url).pipe(catchError(err => of([])));
    }

    //Teams Hackathon 2021
    public get teamsCalendar$(): Observable<any> {
        const url = `${environment.apiUrl}/calendar/teams`;
        return this.http.get<any>(url).pipe(catchError(err => of([])));
    }

    public fetchCalendar(calendar: ResourcesCalendar): Observable<ResourcesCalendar> {
        if (!this._calendar$) {
            this._calendar$ = forkJoin(this.schoolCalendar$, this.canvasCalendar$, this.teamsCalendar$)
                .pipe(
                    tap(([school, canvas, teams]) => {
                        this.log('calendar$', 'Fetched calendar summary', LogCategory.SubscriptionData);
                        var data = [...school, ...canvas, ...teams];
                        calendar.buttonArray = [];
                        calendar.filterCanvas.splice(0, calendar.filterCanvas.length);
                        calendar.filterProgram.splice(0, calendar.filterProgram.length);
                        calendar.filterSchool.splice(0, calendar.filterSchool.length);
                        calendar.filterTeams.splice(0, calendar.filterTeams.length);
                        calendar.calendarEventSource.splice(0, calendar.calendarEventSource.length);

                        var meetingNumber = 0;
                        const dateOptions = { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' } as const;
                        const timeOptions = { hour: 'numeric', minute: 'numeric', hour12: true } as const;

                        for (let meeting of data) {
                            var start = new Date(meeting.StartTime);
                            var end = new Date(meeting.EndTime);

                            var eventTime = start.toLocaleString('en-US', timeOptions);
                            if (eventTime != end.toLocaleString('en-US', timeOptions))
                                eventTime += " - " + end.toLocaleString('en-US', timeOptions);

                            var eventDate = start.toLocaleDateString('en-US', dateOptions);
                            if (eventDate != end.toLocaleDateString('en-US', dateOptions))
                                eventDate += " - " + start.toLocaleDateString('en-US', dateOptions);

                            var calendarItem = new CalendarItem(
                                CalendarItemType.standard,
                                meeting.Title,
                                start,
                                meeting.EndTime,
                            );

                            calendarItem.allDay = meeting.AllDay;
                            calendarItem.url = meeting.Link;
                            calendarItem.description = eventDate;

                            if (meeting.Description != null && meeting.Description.trim().length != 0) {
                                var description = meeting.Description;

                                if (meeting.Description.includes(" - "))
                                    description = meeting.Description.split(' - ').slice(1).join(' - ');

                                calendarItem.description += "<br/>" + description;
                            }


                            var toolTip = eventTime + "<br/>" + calendarItem.description;

                            //  Change start time, title, and tooltip if it's a Canvas assignment
                            if ((meeting.Source == "canvas") && (meeting.CanvasType == "assignment") && (meeting.StartTime == meeting.EndTime)) {
                                start = new Date(start.setMinutes(start.getMinutes() - 60));
                                calendarItem.title = meeting.Title + " (Due " + eventTime + " )";
                                toolTip = calendarItem.description;
                            }

                            calendarItem.extendedProps = {
                                tooltip: toolTip,
                                id: meetingNumber,
                                organizer: meeting.Organizer,
                                organizerEmail: meeting.OrganizerEmail,
                                status: meeting.MeetingStatus
                            };

                            meetingNumber++;

                            if (meeting.Source == "canvas")
                                calendar.filterCanvas.push(calendarItem)

                            if (meeting.Source == "program")
                                calendar.filterProgram.push(calendarItem)

                            if (meeting.Source == "schoolCalendar")
                                calendar.filterSchool.push(calendarItem)

                            if (meeting.Source == "teams")
                                calendar.filterTeams.push(calendarItem)

                        }

                        if (calendar.calendarCanvas.events.length > 0) {
                            calendar.calendarEventSource.push(calendar.calendarCanvas);
                            calendar.displayCanvas = true;
                            calendar.buttonArray.push('canvas');
                        }
                        if (calendar.calendarProgram.events.length > 0) {
                            calendar.calendarEventSource.push(calendar.calendarProgram);
                            calendar.displayProgram = true;
                            calendar.buttonArray.push('program');
                        }
                        if (calendar.calendarSchool.events.length > 0) {
                            calendar.calendarEventSource.push(calendar.calendarSchool);
                            calendar.displaySchool = true;
                            calendar.buttonArray.push('school');
                        }
                        if (calendar.calendarTeams.events.length > 0) {
                            calendar.calendarEventSource.push(calendar.calendarTeams);
                            calendar.displayTeams = true;
                            calendar.buttonArray.push('teams');
                        }
                        calendar.defaultHeader.left = calendar.buttonArray.join();
                    }),
                    takeUntil(this._reload$),
                    shareReplay(1),

                );
        }
        return this._calendar$;
    }

    public fetchAgenda(agendaCalender: AgendaCalendar) {
        if (!this._agendaCalendar$) {
            this._agendaCalendar$ = forkJoin(this.schoolCalendar$, this.canvasCalendar$, this.teamsCalendar$)
                .pipe(
                    tap(([school, canvas, teams]) => {
                        var data = [...school, ...canvas, ...teams];

                        agendaCalender.agendaCanvas.splice(0, agendaCalender.agendaCanvas.length);
                        agendaCalender.agendaSchool.splice(0, agendaCalender.agendaSchool.length);
                        agendaCalender.agendaTeams.splice(0, agendaCalender.agendaTeams.length);

                        const dateOptions = { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' } as const;

                        var dataFiltered = data.filter((a) => (new Date(a.StartTime)).toLocaleDateString('en-US', dateOptions) === (new Date()).toLocaleDateString('en-US', dateOptions));

                        for (let meeting of dataFiltered) {

                            if (meeting.Source == "canvas")
                                agendaCalender.agendaCanvas.push(meeting)

                            if (meeting.Source == "program")
                                agendaCalender.agendaProgram.push(meeting)

                            if (meeting.Source == "schoolCalendar")
                                agendaCalender.agendaSchool.push(meeting)

                            if (meeting.Source == "teams")
                                agendaCalender.agendaTeams.push(meeting)
                        }
                        //  Teams items weren't returning in the approprite order
                        agendaCalender.agendaTeams.sort((a, b) => (a.StartTime > b.StartTime) ? 1 : -1)
                    }),
                    takeUntil(this._reload$),
                    shareReplay(1),
                );

        }
        return this._agendaCalendar$
    }

    public update() {
        this.log('update', 'Calendar updated');
        this._reload$.next();
        this._calendar$ = null;
    }


}

export class AgendaCalendar {

    public static instance: AgendaCalendar;

    agendaCanvas: any[] = [];
    agendaProgram: any[] = [];
    agendaSchool: any[] = [];
    agendaTeams: any[] = [];



    //Singleton pattern
    static getInstance(): AgendaCalendar {
        if (!AgendaCalendar.instance) {
            AgendaCalendar.instance = new AgendaCalendar();
        }

        return AgendaCalendar.instance;
    }

}


export class ResourcesCalendar {

    defaultView: string;
    mobileView: string;
    teamsView: string;
    defaultHeader: any;
    mobileHeader: any;
    mobileWindowSize: number;
    currentSize: number;

    filterCanvas: any[] = [];
    filterProgram: any[] = [];
    filterSchool: any[] = [];
    filterTeams: any[] = [];
    calendarEventSource: any[] = [];

    displayCanvas: boolean = true;
    displayProgram: boolean = true;
    displaySchool: boolean = true;
    displayTeams: boolean = true;

    minTime: string = "00:00:00";
    maxTime: string = "24:00:00";
    titleFormat: object = {};
    dayHeaderFormat: object = {};

    views: any;
    customButtons: any;
    buttonArray: any[];


    calendarCanvas: GroupedCalendarItems;
    calendarProgram: GroupedCalendarItems;
    calendarSchool: GroupedCalendarItems;
    calendarTeams: GroupedCalendarItems;

    calendarEvent$ = new Subject<any>();

    private static instance: ResourcesCalendar;

    private constructor() {
        this.views = {
            dayGrid7: {
                type: CalendarViews.timeGridWeek,
                duration: { days: 7 },
                buttonText: 'Calendar view'
            },
            listWeek7: {
                type: CalendarViews.listWeek,
                duration: { days: 7 },
                buttonText: 'List view'
            },
            singleDay: {
                type: CalendarViews.timeGridDay,
                buttonText: 'Day',
                click: () => this.calendarEventReload(event),
            },
            workWeek: {
                type: CalendarViews.timeGridWeek,
                buttonText: 'Work week',
                click: () => this.calendarEventReload(event),
            },
            fullWeek: {
                type: CalendarViews.timeGridWeek,
                buttonText: 'Week',
                click: () => this.calendarEventReload(event),
            },

        }

        this.customButtons = {
            canvas: {
                text: 'Canvas',
                click: () => this.calendarEventReload(event),
                active: this.displayCanvas
            },
            program: {
                text: 'Program',
                click: () => this.calendarEventReload(event),
                active: this.displayProgram
            },
            school: {
                text: 'School',
                click: () => this.calendarEventReload(event),
                active: this.displaySchool
            },
            teams: {
                text: 'Personal',
                click: () => this.calendarEventReload(event),
                active: this.displayTeams
            }
        }

        this.defaultView = 'dayGrid7';
        this.mobileView = 'listWeek7';
        this.teamsView = 'fullWeek';

        this.defaultHeader = {
            center: CalendarTitle.date,
            right: 'dayGrid7,listWeek7'
        };
        this.mobileHeader = {
            center: CalendarTitle.date,
            right: 'listWeek7'
        };

        this.mobileWindowSize = 1024;

        var canvasColor = "#FFF3CD";
        var programColor = "#80C0EA";
        var schoolColor = "#A7C796";
        var teamsColor = "#FFA141";

        this.calendarCanvas = new GroupedCalendarItems(this.filterCanvas, canvasColor, 'black', canvasColor, 'canvas', 'canvasGroup');
        this.calendarProgram = new GroupedCalendarItems(this.filterProgram, programColor, 'black', programColor, 'program', 'programGroup');
        this.calendarSchool = new GroupedCalendarItems(this.filterSchool, schoolColor, 'black', schoolColor, 'school', 'schoolGroup');
        this.calendarTeams = new GroupedCalendarItems(this.filterTeams, teamsColor, 'black', teamsColor, 'teams', 'teamsGroup');
    }

    //Singleton pattern
    static getInstance(): ResourcesCalendar {
        if (!ResourcesCalendar.instance) {
            ResourcesCalendar.instance = new ResourcesCalendar();
        }

        return ResourcesCalendar.instance;
    }

    calendarEventReload(event) {
        this.calendarEventSource = [];
        event.target.classList.toggle('fc-button-active');

        //Switches the stored setting value and the custom button value
        if (event.target.innerText == "Canvas") {
            this.displayCanvas = !this.displayCanvas;
            this.customButtons.canvas.active = !this.customButtons.canvas.active;
        }

        if (event.target.innerText == "Program") {
            this.displayProgram = !this.displayProgram;
            this.customButtons.program.active = !this.customButtons.program.active;
        }

        if (event.target.innerText == "School") {
            this.displaySchool = !this.displaySchool;
            this.customButtons.school.active = !this.customButtons.school.active;
        }


        if (event.target.innerText == "Personal") {
            this.displayTeams = !this.displayTeams;
            this.customButtons.teams.active = !this.customButtons.teams.active;
        }

        //Displays selected data
        if (this.displayCanvas)
            this.calendarEventSource.push(this.calendarCanvas);

        if (this.displayProgram)
            this.calendarEventSource.push(this.calendarProgram);

        if (this.displaySchool)
            this.calendarEventSource.push(this.calendarSchool);

        if (this.displayTeams)
            this.calendarEventSource.push(this.calendarTeams);

        this.calendarEvent$.next(this.calendarEventSource);
    }
}
